import { Component, OnInit } from '@angular/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { HttpRequestService, LocalStorageService } from 'src/app/core/services';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { toNumber } from 'ng-zorro-antd/core/util';
import { TestSeries } from 'src/app/core/interfaces/test-series';

@Component({
  selector: 'app-test-analytics',
  templateUrl: './test-analytics.component.html',
  styleUrls: ['./test-analytics.component.scss'],
})
export class TestAnalyticsComponent implements OnInit {
  allAnswerSheets: any[] = [];
  totalDataCount = 0;
  pageSize = 10;
  pageIndex = 1;
  loading = false;
  searchSubject: Subject<string> = new Subject<string>();
  search = '';

  // Filter properties
  selectedTestSeries: string = '';
  selectedTest: string = '';
  selectedUser: string = '';
  startDate: any = null;
  endDate: any = null;

  // Dropdown data
  allTestSeries: any[] = [];
  allTests: any[] = [];
  allUsers: any[] = [];

  // Search subjects for dropdown searches
  searchTestSeries: Subject<string> = new Subject<string>();
  searchTest: Subject<string> = new Subject<string>();
  searchUser: Subject<string> = new Subject<string>();

  testSeriesSearch = '';
  testSearch = '';
  userSearch = '';
  selectedSubmissionStatus: string | null = null;
  dateRange = null;

  constructor(
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
    private localStorageService: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.initializeSearchSubjects();
    this.getAllTestSeries();
    this.getAllTests();
    this.getAllUsers();
    this.getAnswerSheets();
  }

  initializeSearchSubjects(): void {
    this.searchSubject.pipe(debounceTime(800)).subscribe((success) => {
      this.search = success;
      this.pageIndex = 1;
      this.getAnswerSheets();
    });

    this.searchTestSeries.pipe(debounceTime(800)).subscribe((success) => {
      this.testSeriesSearch = success;
      this.getAllTestSeries();
    });

    this.searchTest.pipe(debounceTime(800)).subscribe((success) => {
      this.testSearch = success;
      this.getAllTests();
    });

    this.searchUser.pipe(debounceTime(800)).subscribe((success) => {
      this.userSearch = success;
      this.getAllUsers();
    });
  }

  getAllTestSeries(): void {
    console.log('Fetching test series with search:', this.testSeriesSearch);
    const params: any = {
      skip: 0,
      limit: 100,
      approveStatus: 'approved',
    };
    if (this.testSeriesSearch) {
      params.search = this.testSeriesSearch;
    } else {
      delete params.search;
    }
    this.httpRequestService
      .request('get', 'test-series/for-list', params)
      .subscribe(
        (result) => {
          this.loading = false;
          const data: TestSeries[] = result.data;
          this.allTestSeries = data;
          this.totalDataCount = result.totalCount;
        },
        (err) => {
          this.loading = false;
        },
      );
  }

  getAllTests(): void {
    const params: any = {
      skip: 0,
      limit: 100,
      status: true,
    };
    if (this.testSearch) {
      params.search = this.testSearch;
    } else {
      delete params.search;
    }
    if (this.selectedTestSeries) {
      params.testSeries = this.selectedTestSeries;
    }
    this.httpRequestService
      .request('get', 'tests/get-tests-for-list', params)
      .subscribe(
        (result: any) => {
          this.allTests = result.data || [];
        },
        (err: any) => {
          console.error('Error fetching tests:', err);
        },
      );
  }

  getAllUsers(): void {
    const params: any = {
      skip: 0,
      limit: 100,
      status: true,
    };
    if (this.userSearch) {
      params.search = this.userSearch;
    } else {
      delete params.search;
    }
    this.httpRequestService.request('get', 'users', params).subscribe(
      (result: any) => {
        this.allUsers = result.data || [];
      },
      (err: any) => {
        console.error('Error fetching users:', err);
      },
    );
  }
  onChangeDateRange(date: any) {
    this.startDate = date[0];
    this.endDate = date[1];
    this.getAnswerSheets();
  }
  getAnswerSheets(): void {
    let params: any = {
      skip: (this.pageIndex - 1) * this.pageSize,
      limit: this.pageSize,
    };
    if (this.selectedSubmissionStatus !== null) {
      params.isSubmitted = this.selectedSubmissionStatus;
    } else {
      delete params.isSubmitted;
    }
    if (this.selectedTestSeries) {
      params.testSeries = this.selectedTestSeries;
    } else {
      delete params.testSeries;
    }
    if (this.selectedTest) {
      params.test = this.selectedTest;
    } else {
      delete params.test;
    }
    if (this.selectedUser) {
      params.user = this.selectedUser;
    } else {
      delete params.user;
    }
    if (this.startDate && this.endDate) {
      params.startDate = this.startDate;
      params.endDate = this.endDate;
    } else {
      delete params.startDate;
      delete params.endDate;
    }
    // if (this.search) {
    //   params.search = this.search;
    // }

    this.loading = true;
    this.httpRequestService.request('get', 'answer-sheets', params).subscribe(
      (result: any) => {
        this.loading = false;
        this.allAnswerSheets = result.data || [];
        this.totalDataCount = result.totalCount || 0;
      },
      (err: any) => {
        this.loading = false;
        this.notificationService.error(
          'Error',
          'Failed to fetch answer sheets',
        );
        console.error('Error fetching answer sheets:', err);
      },
    );
  }

  getExamTypeClass(type: string): string {
    const map: any = {
      NORMAL: 'tag-blue',
      MOCK: 'tag-green',
      PRACTICE: 'tag-orange',
    };
    return map[type] || '';
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { pageSize, pageIndex } = params;
    this.pageIndex = pageIndex;
    this.pageSize = pageSize;
    this.getAnswerSheets();
  }

  onChangeTestSeries(): void {
    this.selectedTest = '';
    this.allTests = [];
    this.pageIndex = 1;
    this.getAllTests();
    this.getAnswerSheets();
  }

  onChangeTest(): void {
    this.pageIndex = 1;
    this.getAnswerSheets();
  }

  onChangeUser(): void {
    this.pageIndex = 1;
    this.getAnswerSheets();
  }

  onDateRangeChange(): void {
    this.pageIndex = 1;
    this.getAnswerSheets();
  }

  onFilterChange() {
    this.pageIndex = 1; // reset pagination
    this.getAnswerSheets();
  }
  // resetFilters(): void {
  //   this.selectedTestSeries = '';
  //   this.selectedTest = '';
  //   this.selectedUser = '';
  //   this.startDate = null;
  //   this.endDate = null;
  //   this.search = '';
  //   this.pageIndex = 1;
  //   this.getAnswerSheets();
  // }

  searchAnswerSheets(event: string): void {
    this.searchSubject.next(event);
  }

  searchTestSeriesForDropdown(event: string): void {
    this.searchTestSeries.next(event);
  }

  searchTestForDropdown(event: string): void {
    this.searchTest.next(event);
  }

  searchUserForDropdown(event: string): void {
    this.searchUser.next(event);
  }

  // Helper method to convert seconds to HH:MM:SS format
  formatTime(minutes: number): string {
    if (!minutes) return '0m';

    const h = Math.floor(minutes / 60);
    const m = minutes % 60;

    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  }

  // Helper method to get exam type badge color
  getExamTypeColor(examType: string): string {
    const colorMap: any = {
      NORMAL: 'blue',
      MOCK: 'green',
      PRACTICE: 'orange',
    };
    return colorMap[examType] || 'default';
  }
}
